'use memo';

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import IconButton from '@/components/Button/IconButton';
import Combobox from '@/components/Combobox';
import Icon from '@/components/Icon/Icon';
import { Text } from '@/components/Typography/Text';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';
import type { CodeNameEntry } from './useBanks';
import { useBanks } from './useBanks';
const BANK_CODE_PATTERN = /^\d{4}$/;
export interface SelectBankProps {
  value?: CodeNameEntry;
  onChange: (entry?: CodeNameEntry) => void;
  allowEmpty?: boolean;
  disabled?: boolean;
  rootClassName?: string;
  width?: number;
}
const SelectBank = ({
  value,
  onChange,
  allowEmpty,
  disabled,
  rootClassName,
  width
}: SelectBankProps) => {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'component.selectBank'
  });
  const [open, setOpen] = useState(false);
  const [rawInput, setRawInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const popoverContentId = useId();
  const {
    banks,
    isFetching,
    error
  } = useBanks(searchText);
  const autoConfirmedRef = useRef(false);
  const onChangeRef = useRef(onChange);
  // oxlint-disable-next-line react/refs -- baseline
  onChangeRef.current = onChange;
  const options = useMemo(() => banks.map(entry => ({
    value: entry.code,
    label: `${entry.name} (${entry.code})`
  })), [banks]);
  const canClear = Boolean(allowEmpty && !disabled && value);
  const displayLabel = value ? `${value.name} (${value.code})` : t('placeholder');
  const handleValueChange = (code: string | null) => {
    if (code) {
      const entry = banks.find(b => b.code === code);
      if (entry) {
        onChange(entry);
      }
    } else {
      onChange(undefined);
    }
    setOpen(false);
  };
  const debouncedSetSearch = useDebouncedCallback((text: string) => {
    setSearchText(text);
    autoConfirmedRef.current = false;
  }, []);
  const handleSearchChange = (text: string) => {
    setRawInput(text);
    debouncedSetSearch(text);
  };
  useEffect(() => {
    if (!open || !searchText || isFetching || autoConfirmedRef.current || searchText !== rawInput || !BANK_CODE_PATTERN.test(searchText) || banks.length !== 1 || banks[0].code !== searchText) {
      return;
    }
    autoConfirmedRef.current = true;
    onChangeRef.current(banks[0]);
    setOpen(false);
  }, [banks, searchText, rawInput, open, isFetching]);
  return <>
      <Popover open={open} onOpenChange={newOpen => {
      setOpen(newOpen);
      if (newOpen) {
        setRawInput('');
        setSearchText('');
        debouncedSetSearch('');
        autoConfirmedRef.current = false;
      }
    }}>
        <div className={cn('relative inline-block', rootClassName)} style={rootClassName ? undefined : {
        width: width ?? 280
      }}>
          <PopoverTrigger asChild>
            <Button variant="outline" role="combobox" aria-expanded={open} aria-controls={popoverContentId} className="relative w-full justify-start pr-10" disabled={disabled}>
              <span className={cn('min-w-0 truncate', canClear && 'pr-8')}>
                {displayLabel}
              </span>
              <div className="absolute inset-y-0 right-3 flex items-center">
                <Icon iconName="ChevronDownIcon" className="shrink-0 opacity-50" />
              </div>
            </Button>
          </PopoverTrigger>
          {canClear && <div className="absolute inset-y-0 right-8 flex items-center">
              <IconButton icon="XIcon" variant="mutedGhost" size="ExtraSmall" aria-label={t('clearSelection')} onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            onChange(undefined);
            setOpen(false);
            setSearchText('');
            setRawInput('');
          }} onPointerDown={e => {
            e.preventDefault();
            e.stopPropagation();
          }} />
            </div>}
        </div>
        <PopoverContent id={popoverContentId} className="w-72 p-0">
          <Combobox type="single" options={options} value={value?.code ?? ''} onValueChange={handleValueChange} isFetching={isFetching} onSearchChange={handleSearchChange} searchPlaceholder={t('searchPlaceholder')} noResultsMessage={t('noResultsMessage')} showCheckbox={false} />
        </PopoverContent>
      </Popover>
      {error && <Text variant="labelSmall" className="mt-1 text-error-on-neutral-fill">
          {t('fetchError')}
        </Text>}
    </>;
};
export default SelectBank;